import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    output,
    Renderer2,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import Highcharts, { EventCallbackFunction } from 'highcharts/highstock';
import isEmpty from 'lodash.isempty';
import { Observable, Subscription } from 'rxjs';

import { setFundChartSecurities } from 'ChartApp/ts/features/fund-chart-page-shared/state/reducers/securities/securities.actions';

import {
    ADDITIONAL_LEGEND_HEIGHT,
    BASE_LEGEND_HEIGHT,
    EVENT_LABEL_HTML,
    HIGHCHARTS_FUND_DEFAULT_OPTIONS,
    TOOLTIP_STYLE_BOX,
} from '../../constants';
import { FundChartAnnotationOptions } from 'ChartApp/ts/types/fund-chart-common.type';
import { FundChartRootStore } from 'ChartApp/ts/features/fund-chart-page-shared/interfaces/fund-chart-store.interface';
import { AxisExtremes } from 'ChartApp/ts/interfaces/chart-common.interface';
import {
    FundamentalChartData,
    FundChartAIKeys,
    FundChartAnnotation,
    FundChartChartOptions,
    FundChartConfigurableOptions,
    FundChartData,
    FundChartDimensions,
    FundChartState,
    FundChartViewDataError,
} from 'ChartApp/ts/interfaces/fund-chart.interface';
import { TooltipData } from 'ChartApp/ts/interfaces/fund-chart-common.interface';

import { ChartZoomService } from 'ChartApp/ts/features/charts-shared/services/chart-zoom.service';
import { FundChartService } from 'ChartApp/ts/services/fund-chart.service';

import { isSameDateUTC } from 'Angular/utils/date';
import deepEqual from 'Angular/utils/equal/deep-equal';
import { isMobile } from 'Angular/utils/media';
import { observeContainerResize } from 'ChartApp/ts/utils/chart-container-resize';

import YchartFundInteractive from 'ChartApp/ts/features/fundamental-chart/ychart_fund_interactive';

@Component({
    standalone: false,
    selector: 'ycn-fundamental-chart',
    templateUrl: './fundamental-chart.template.html',
    providers: [ChartZoomService],
})
export class FundamentalChartComponent implements OnInit, OnChanges, OnDestroy {
    @ViewChild('chart') chartElement!: ElementRef;

    @Input() axisValue?: AxisExtremes | {} = {};

    @Input() chartId?: string = '';

    @Input() chartDataUrl: string = '';

    @Input() chartFormData?: FundChartState = {} as FundChartState;

    @Input() chartJsonData?: FundamentalChartData = {} as FundamentalChartData;

    @Input() chartAnnotations: Highcharts.AnnotationsOptions[] = [];

    @Input() annotationsHidden: boolean = false;

    readonly showAnnotationsToggle = input(false);

    @Input() noDataClass: string = '';

    @Input() noDataHtml: string = '';

    @Input() sidebarOpen: string = '';

    @Input() chartOptions: object = {};

    @Input() configurableOptions!: FundChartConfigurableOptions;

    @Input() showEmptyChart: boolean = false;

    @Input() hideZoomControls: boolean = false;

    @Input() aISummaryKeys: FundChartAIKeys[] = [];

    @Output() chartDataHandler = new EventEmitter<FundamentalChartData>(true);

    @Output() subchartOffsetsHandler = new EventEmitter<number[]>(true);

    @Output() tooltipDataHandler = new EventEmitter<TooltipData>(true);

    @Output() annotationHandler = new EventEmitter<FundChartAnnotation>(true);

    @Output() toolsetToggleHandler = new EventEmitter<boolean>(true);

    @Output() chartClickHandler = new EventEmitter();

    readonly annotationsHiddenChange = output<boolean>();

    @Output() openAISummaryClickEvent = new EventEmitter<{ selectedStartDate: Date; seriesId: string }>();

    annotationEventsEnabled: boolean = true;

    resizeEventBound: boolean = false;

    resizeObserver: ResizeObserver | null = null;

    noDataMessage: string = '';

    getStartedMessage: string = '';

    renderElement!: HTMLElement;

    windowWidth!: number;

    highchartOptions!: FundChartChartOptions;

    chartResponse: FundamentalChartData | null = null;

    defaultOptions: FundChartChartOptions | undefined;

    hostElement: HTMLElement;

    ychartFundInteractive: YchartFundInteractive | undefined;

    REBIND_CHART_RESIZE_EVENT = 'rebindChartResize';

    fetchSubscription: Subscription | null = null;

    chartZoomSubscription: Subscription | null = null;

    mouseOverChart: boolean = false;

    chartLocalId: string = this.chartId ? this.chartId : String(Math.random());

    mouseCoords: { x: number; y: number } | null = null;

    panningOn: boolean = false;

    zoomOnSubscription: Subscription = new Subscription();

    chartClasses: string = '';

    chartStyles: string = '';

    _hideZoomControls: boolean = false;

    constructor(
        private fundChartService: FundChartService,
        public renderer: Renderer2,
        public eltRef: ElementRef,
        private changeDetector: ChangeDetectorRef,
        protected chartZoomService: ChartZoomService,
        private readonly store: Store<{ reducers: FundChartRootStore }>
    ) {
        this.hostElement = this.eltRef.nativeElement;
        this.defaultOptions = HIGHCHARTS_FUND_DEFAULT_OPTIONS;
    }

    ngOnInit(): void {
        this.setupRebindResizeHandler();
        this.zoomOnSubscription = this.chartZoomService.getZoomEvent().subscribe(({ panningOn }) => {
            this.panningOn = panningOn;
        });
    }

    displayChartLoader(): void {
        this.renderer.addClass(this.hostElement, this.noDataClass);
        this.chartElement.nativeElement.innerHTML =
            '<div class="loader"><div class="loader-message">Loading ...</div><div class="loader-spinner"></div></div>';
        this.unbindResizeHandler();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.chartId) {
            const chartIdName = this.chartId === '' ? String(Math.random()) : this.chartId;
            this.chartLocalId = `${chartIdName}s1`;
        }
        if (changes.noDataHtml) this.noDataMessage = changes.noDataHtml.currentValue;
        this.noDataMessage = this.noDataHtml;
        if (!this.renderElement) {
            this.renderElement = this.hostElement.parentElement!;
        }

        if (this.chartElement) {
            if (this.defaultOptions?.chart) {
                this.defaultOptions.chart.renderTo = this.chartElement.nativeElement;
            }
        }

        const dataSource = changes.chartDataUrl?.currentValue ? changes.chartDataUrl : changes.chartFormData;
        if (dataSource?.currentValue && !deepEqual(dataSource.currentValue, dataSource.previousValue)) {
            this._hideZoomControls = true;
            if (this.fetchSubscription !== null) {
                this.fetchSubscription.unsubscribe();
            }

            this.fetchSubscription = this.getChartData().subscribe({
                next: (rsp: FundamentalChartData) => {
                    // Set chartResponse for the resize handler
                    this.chartResponse = rsp;
                    this.store.dispatch(setFundChartSecurities({ fundChartSecurities: rsp.params.securities }));
                    if (this.chartDataHandler) this.chartDataHandler.emit({} as FundamentalChartData);
                },
                error: (err: FundChartViewDataError) => {
                    this.displayDataError(err);
                    this._hideZoomControls = false;
                },
                complete: () => {
                    // Set chartResponse for the resize handler
                    this.renderChart(this.chartResponse);
                    this._hideZoomControls = this.hideZoomControls;
                },
            });
        } else if (dataSource && !dataSource.currentValue) {
            this.resetChart();
            this.displayNoData();
            if (this.chartDataHandler) this.chartDataHandler.emit({} as FundamentalChartData);
        }

        if (changes.chartJsonData && changes.chartJsonData.currentValue.chart_data) {
            this.renderChart(changes.chartJsonData.currentValue);
        }
        if (changes.sidebarOpen && changes.sidebarOpen.currentValue !== undefined) {
            setTimeout(() => {
                this.redrawChart();
            }, 200);
        }
        if (
            changes.chartOptions?.currentValue &&
            !deepEqual(changes.chartOptions.currentValue, changes.chartOptions.previousValue)
        ) {
            // only redraw the chart if a chart is currently displayed
            if (this.chartDataUrl || Object.keys(this.chartFormData ?? {}).length) this.redrawChart();
        }

        if (changes.annotationsHidden) {
            this.redrawChart();
        }
    }

    ngOnDestroy(): void {
        this.unbindResizeHandler();
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this.fetchSubscription !== null) {
            this.fetchSubscription.unsubscribe();
        }
        if (this.chartZoomSubscription !== null) {
            this.chartZoomSubscription.unsubscribe();
        }
        this.zoomOnSubscription.unsubscribe();
    }

    deleteAnnotation(id: number): void {
        if (this.ychartFundInteractive?.stockChart && this.ychartFundInteractive?.stockChart.annotations.length) {
            this.ychartFundInteractive.stockChart.navigationBindings.deselectAnnotation();
            this.ychartFundInteractive.stockChart.annotations[id].destroy();
            this.ychartFundInteractive.stockChart.annotations.splice(id, 1);
        }
    }

    annotationEventCallback = (event: Event): void => {
        if (!this.annotationEventsEnabled) {
            return;
        }
        // @ts-ignore
        const { chart, userOptions, options } = event.target;
        const id = chart.annotations.indexOf(event.target);
        let clicksToCompleteAnnotation: number = 1;
        // This is probably a bug in highcharts, but when the height
        // of a parallelChannel annotation is changed,
        // the userOptions object is not updated with the new height.
        // We need to get the new height from the event here and add it to the
        // userOptions object before emitting from this.annotationHandler.
        if (options.langKey === 'parallelChannel') {
            userOptions.typeOptions.height = options.typeOptions.height;
            clicksToCompleteAnnotation = 2;
        }
        this.annotationHandler.emit({
            chartAnnotation: userOptions as FundChartAnnotationOptions,
            id,
        });
        // Some line and shape annotations take multiple clicks to complete their
        // placement on the chart, but after the first click Highcharts starts
        // firing 'afterUpdate' events constantly until the annotation placement is finished.
        // Handling all of these events causes a big performance drop, and we only need to update the
        // state once after the annotation placement is complete. So after an 'add' event,
        // we ignore other annotation events until the required number of clicks
        // to place the annotation have occurred.
        if (event.type === 'add') {
            this.annotationEventsEnabled = false;
            let clicksAfterAdd = 0;
            const enableEventsCallback = (): void => {
                clicksAfterAdd += 1;
                if (clicksAfterAdd >= clicksToCompleteAnnotation) {
                    this.annotationEventsEnabled = true;
                    this.hostElement.removeEventListener('mousedown', enableEventsCallback);
                }
            };
            this.hostElement.addEventListener('mousedown', enableEventsCallback);
        }
    };

    updateAnnotation(annotation: Highcharts.AnnotationsOptions, id: number): void {
        if (this.ychartFundInteractive?.stockChart) {
            this.annotationEventsEnabled = false;
            this.ychartFundInteractive.stockChart.annotations[id].update(annotation);
            this.annotationEventsEnabled = true;
        }
    }

    getChartData(): Observable<FundamentalChartData> {
        this.resetChart();
        this.displayChartLoader();
        if (this.chartDataHandler) this.chartDataHandler.emit({} as FundamentalChartData);

        const { chartDataUrl, chartFormData } = this;
        const maxPoints = parseInt(String(this.renderElement.clientWidth * 0.9), 10);
        if (chartFormData && !isEmpty(chartFormData)) {
            const chartDataPostParams = maxPoints !== 0 ? { ...chartFormData, maxPoints } : chartFormData;
            return this.fundChartService.getChartDataFromState(chartDataPostParams);
        }

        const chartDataGetParams = maxPoints !== 0 ? `${chartDataUrl}&maxPoints=${maxPoints}` : chartDataUrl;
        return this.fundChartService.getChartDataFromUrl(chartDataGetParams);
    }

    displayNoDataLabel(): void {
        if (this.noDataMessage === '') {
            this.noDataMessage = `<span>No data to display.</span>`;
        }
        this.displayNoData();
    }

    resetChart(): void {
        // Remove tooltip and offset data
        this.tooltipDataHandler.emit([]);
        this.subchartOffsetsHandler.emit([]);
        // Manually run change detection so all components are updated with blank tooltip and
        // offset data. An ExpressionChangedAfterItHasBeenCheckedError will be thrown otherwise.
        this.changeDetector.detectChanges();
        // Always destroy the chart if it exists
        if (this.ychartFundInteractive) this.ychartFundInteractive.destroyStockChart();
    }

    displayNoData(): void {
        // Remove chartData so that if the chart wasn't cleaned up nicely,
        // it doesn't reappear
        this.renderer.addClass(this.hostElement, this.noDataClass);
        this.chartClasses = '';
        this.chartStyles = '';
        this.chartElement.nativeElement.innerHTML = this.noDataMessage;
        this.toolsetToggleHandler.emit(false);
        this.unbindResizeHandler();
        this.chartZoomService.clearChart();
    }

    displayDataError(response: FundChartViewDataError): void {
        // // If we get an error due to a bad request, write the error message
        if (response.error && response.status === 400) {
            this.noDataMessage = `<span>${response.error.error}</span>`;
        } else if (response.error && response.status === 404) {
            this.noDataMessage = `<span>Chart not found.</span>`;
        } else {
            this.noDataMessage = this.getStartedMessage;
        }
        this.displayNoData();
    }

    clearChartContainer(): void {
        this.renderer.removeClass(this.hostElement, this.noDataClass);
        this.chartClasses = '';
        this.chartStyles = '';
        this.chartElement.nativeElement.innerHTML = '';
    }

    redrawChart(): void {
        setTimeout(() => {
            this.renderChart(this.chartResponse);
            this.windowWidth = window.innerWidth;
        });
    }

    setEmptyChart(chartInfo: FundamentalChartData | null): void {
        // Hide the latest value label on the y-axis
        this.configurableOptions.showLatestValueLabels = false;

        // Add fake data so that the x-axis appears
        if (chartInfo?.chart_data[0] && chartInfo?.chart_data[0][0] && chartInfo.start_date) {
            const startDate = new Date(chartInfo.start_date as string);
            startDate.setMinutes(-startDate.getTimezoneOffset());
            const endDate = chartInfo.end_date ? new Date(chartInfo.end_date as string) : new Date();
            endDate.setHours(0, 0, 0, 0);
            endDate.setMinutes(-endDate.getTimezoneOffset());

            // eslint-disable-next-line no-param-reassign
            chartInfo.chart_data[0][0].raw_data = [
                [startDate.getTime(), 999],
                [endDate.getTime(), 999],
            ];
            for (let i = 1; i < chartInfo.chart_data[0].length; i += 1) {
                // eslint-disable-next-line no-param-reassign
                chartInfo.chart_data[0][i].raw_data = [];
            }
        }
    }

    renderChart(response: FundamentalChartData | null): void {
        if (!response) return;
        this.chartResponse = response;
        const chartInfo = response;
        let { chartOptions } = this;

        if (this.showEmptyChart) {
            this.setEmptyChart(chartInfo);
            // Set y-axis values
            chartOptions = {
                yAxis: {
                    min: 10,
                    max: 100,
                },
            };
        }
        // Set our highchart options and configurable options with deep copies
        this.highchartOptions = Highcharts.merge(HIGHCHARTS_FUND_DEFAULT_OPTIONS, chartOptions);

        this.highchartOptions = Highcharts.merge(this.highchartOptions, {
            navigation: {
                annotationsOptions: {
                    events: {
                        add: this.annotationEventCallback,
                        afterUpdate: this.annotationEventCallback,
                    },
                },
            },
        });

        this.setHighchartsAISummaryEvents();

        const adjustLegend =
            isMobile() &&
            this.configurableOptions.tooltipStyle !== TOOLTIP_STYLE_BOX &&
            this.configurableOptions.dynamicHeight;

        // get chartDimensions using window information
        const chartDimensions = this.getChartDimensions(chartInfo.chart_data, adjustLegend);
        const numCharts = chartInfo.chart_data.length;
        if (numCharts > 0) {
            if (this.chartDataHandler) {
                this.chartDataHandler.emit(chartInfo);
            }
            this.resetChart();
            this.clearChartContainer();

            if (this.fundChartService.chartHasNoData(chartInfo)) {
                this.displayNoDataLabel();
                return;
            }

            this.setTooltipData();

            this.highchartOptions = this.chartZoomService.addZoomOptions(this.highchartOptions);
            this.ychartFundInteractive = new YchartFundInteractive(
                { ...this.highchartOptions },
                { ...this.configurableOptions, adjustLegend }
            );

            this.chartClasses = 'flex-auto full-height';
            this.chartStyles = 'height: 0; overflow: hidden;';
            // Don't attempt to render the chart if the chart element is not in the DOM
            if (this.ychartFundInteractive && document.getElementById(this.chartLocalId)) {
                this.annotationEventsEnabled = false;
                const annotationsToAdd: Highcharts.AnnotationsOptions[] = this.annotationsHidden
                    ? []
                    : this.chartAnnotations.map((annotation) => {
                          return {
                              ...annotation,
                              events: {
                                  add: this.annotationEventCallback as EventCallbackFunction<Highcharts.Annotation>,
                                  afterUpdate: this
                                      .annotationEventCallback as EventCallbackFunction<Highcharts.Annotation>,
                              },
                          };
                      });
                this.ychartFundInteractive.createStockChart(
                    this.chartLocalId,
                    chartInfo,
                    this.chartElement.nativeElement,
                    chartDimensions,
                    annotationsToAdd
                );
                if (this.ychartFundInteractive.stockChart) {
                    this.chartZoomService.setChart(this.ychartFundInteractive.stockChart);
                    if (this.axisValue) {
                        this.chartZoomService.zoomIn(this.axisValue);
                    }
                }
                this.annotationEventsEnabled = true;
                // Update subchartOffsets to be passed to tooltip component from parent
                this.subchartOffsetsHandler.emit(this.ychartFundInteractive.subchartOffsets);
            }

            this.bindResizeHandler();
            this.setupResizeObserver();
            this.toolsetToggleHandler.emit(true);
        } else {
            this.displayNoData();
            if (this.chartDataHandler) this.chartDataHandler.emit({} as FundamentalChartData);
        }
    }

    getChartDimensions(chartObjects: FundChartData[][], adjustLegend: boolean): FundChartDimensions {
        const chartWidth = this.renderElement.clientWidth;
        let chartHeight;
        if (this.configurableOptions.dynamicHeight) {
            const winHeight = window.innerHeight;
            const chartOffset = this.renderElement.getBoundingClientRect().top;
            // the chartHeight is total window height, minus everything on top
            // and 10px padding on bottom, and 28px footer.
            chartHeight = Math.max(winHeight - chartOffset - 38, this.hostElement.offsetHeight);
        } else {
            chartHeight = this.renderElement.getBoundingClientRect().height;
        }

        // If we are in adjustLegend mode (legend between charts) we need to add extra height
        // that the legend takes up
        let chartMaxHeight;
        if (adjustLegend) {
            let extraHeight = 0;
            for (let i = 0; i < chartObjects.length; i += 1) {
                for (let j = 0; j < chartObjects[i].length; j += 1) {
                    if (j === 0) {
                        extraHeight += BASE_LEGEND_HEIGHT;
                    } else {
                        extraHeight += ADDITIONAL_LEGEND_HEIGHT;
                    }
                }
            }
            chartMaxHeight = Math.round(1.2 * chartWidth) + extraHeight;
        } else {
            chartMaxHeight = Math.round(0.9 * chartWidth);
        }

        return {
            height: chartHeight,
            maxHeight: chartMaxHeight,
            width: chartWidth,
        };
    }

    setTooltipData(): void {
        // We use this when we want a callback to a custom tooltip directive
        // Sets up a tooltipData array to be populated by Highchart on mouseover
        this.configurableOptions.tooltipCallback = (tooltipData: TooltipData) => {
            setTimeout(() => {
                if (this.ychartFundInteractive?.stockChart?.tooltip) {
                    this.tooltipDataHandler.emit(tooltipData);
                }
            });
        };
    }

    setupRebindResizeHandler(): void {
        // listen for other charts instructing this chart to re-bind itself to the resize event
        window.addEventListener(this.REBIND_CHART_RESIZE_EVENT, () => {
            if (this.resizeEventBound) {
                this.bindResizeHandler(true);
            }
        });
    }

    resizeHandler = (): void => {
        // Container-driven resizes are handled by the ResizeObserver (which calls setSize on the live chart).
        // Dynamic-height charts size from the window, so they still need a window-driven redraw.
        if (this.configurableOptions.dynamicHeight) {
            this.redrawChart();
        }
    };

    /**
     * Set up a ResizeObserver for the host element, so we can dynamically resize the chart
     * when the size of the host element changes.
     * @private
     */
    setupResizeObserver(): void {
        if (this.resizeObserver || !this.renderElement) {
            return;
        }
        this.resizeObserver = observeContainerResize(
            this.renderElement,
            // Read the chart at fire time so we always size the current instance.
            (width, height) => this.ychartFundInteractive?.stockChart?.setSize(width, height, false),
            // Dynamic-height charts size from the window, so they keep using the window resize handler.
            { skip: () => this.configurableOptions.dynamicHeight }
        );
    }

    bindResizeHandler(isRebinding = false): void {
        // Calling `.unbind` will cause all of the charts to unbind from the "resize" event.
        // In that case, we have to rebind all of the other charts on the page, which is accomplished by setting
        // `isRebinding=true`.
        if (isRebinding || !this.resizeEventBound) {
            window.addEventListener('resize', this.resizeHandler);
            this.resizeEventBound = true;
        }
    }

    unbindResizeHandler(): void {
        if (this.resizeEventBound) {
            window.removeEventListener('resize', this.resizeHandler);
            this.resizeEventBound = false;
            // calling unbind above will cause all charts in the window to unbind from their resize handlers
            // we must instruct them to re-bind
            // eslint-disable-next-line no-undef
            window.dispatchEvent(new Event(this.REBIND_CHART_RESIZE_EVENT));
        }
    }

    setMouseDownCoords(event: MouseEvent): void {
        this.mouseCoords = {
            x: event.clientX,
            y: event.clientY,
        };
    }

    handleClickOrDrag(event: MouseEvent): void {
        if (!this.panningOn && this.mouseCoords) {
            const { x, y } = this.mouseCoords;
            const mouseUpX = event.clientX;
            const mouseUpY = event.clientY;
            const areCoordsInClickRange = (mouseDown: number, mouseUp: number): boolean =>
                Boolean(Math.abs(mouseDown - mouseUp) < 3);
            const isClick = areCoordsInClickRange(x, mouseUpX) && areCoordsInClickRange(y, mouseUpY);
            if (isClick) {
                this.chartClickHandler.emit();
            }
        }
    }

    private setHighchartsAISummaryEvents(): void {
        const that = this;
        this.highchartOptions = Highcharts.merge(this.highchartOptions, {
            plotOptions: {
                series: {
                    dataLabels: {
                        enabled: true,
                        useHTML: true,
                        formatter() {
                            const pointDate = new Date((this as any).key);
                            const seriesId = (this as any).series.options.id || (this as any).series.options.onSeries;
                            const lastUnderscoreIdx = seriesId.lastIndexOf('_');
                            const calc = seriesId.substring(0, lastUnderscoreIdx);
                            const securityId = seriesId.substring(lastUnderscoreIdx + 1);
                            const seriesPointExists = that.aISummaryKeys.find((aiSummary) => {
                                const currentDate = new Date(aiSummary.startDate);
                                return (
                                    isSameDateUTC(pointDate, currentDate) &&
                                    securityId === aiSummary.securityId &&
                                    calc === aiSummary.calc
                                );
                            });
                            return seriesPointExists ? EVENT_LABEL_HTML : undefined;
                        },
                    },
                },
            },
            chart: {
                events: {
                    render: (event: any) => {
                        const { series } = event.target;
                        const clickEventFunction = (point: any) => (e: MouseEvent) => {
                            const data = {
                                selectedStartDate: new Date(point.key || point.x),
                                seriesId: point.series.options.id!,
                            };
                            this.openAISummaryClickEvent.emit(data);
                            e.stopPropagation();
                        };
                        for (let i = 0; i < series.length; i += 1) {
                            const { points } = series[i];
                            points.forEach((point: any) => {
                                if (point.dataLabel) {
                                    point.dataLabel.div.addEventListener('click', clickEventFunction(point));
                                }
                            });
                        }
                    },
                },
            },
        });
    }
}
